import Box from "@material-ui/core/Box";
import Drawer from "@material-ui/core/Drawer";
import IconButton from "@material-ui/core/IconButton";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemText from "@material-ui/core/ListItemText";
import { useTheme } from "@material-ui/core/styles";
import clsx from "clsx";
import React, { Fragment, useEffect, useMemo, useState } from "react";
import {
  Chart,
  Chat,
  Document,
  Home,
  Paper,
  Search,
  Setting,
  Wallet,
} from "react-iconly";
import { useSelector } from "react-redux";
import { useLocation } from "react-router";
import { Link } from "react-router-dom";
import logoBISmall from "../../../assets/images/logo_bi_small.svg";
import logoBIBlue from "../../../assets/images/logo_sbs_blue.svg";
import { PAGES } from "../../../constants";
import { Menu as MenuType } from "../../../pages/ManageAccess/types";
import { appState } from "../../../redux/reducers/appReducer";
import { getSystemCode } from "../../../services";
import EmptyOrShow from "../../EmptyOrShow";
import { WrapperScrollbar } from "../../WrapperScrollbar";
import { useStyles } from "../index";
import { SidebarUserbox } from "../sidebarUserBox";
import MenuItem from "./MenuItem";
import { getMenuData } from "./services";

type MenuComponentType = {
  changeOpenMenu: () => void;
};
export const Menu = ({ changeOpenMenu }: MenuComponentType) => {
  const theme = useTheme();
  const location = useLocation();
  const classes = useStyles();
  const [isFetching, setIsFetching] = useState(false);
  const [data, setData] = useState<MenuType[] | null>(null);
  const { isMainMenuOpen: open } = useSelector(appState);

  useEffect(() => {
    const getMenu = async () => {
      const menu = await getMenuData(setIsFetching);
      setData(menu);
    };
    getMenu();
  }, []);

  const system = useMemo(() => getSystemCode(), []);

  return (
    <Drawer
      anchor="left"
      open={open}
      onClose={changeOpenMenu}
      variant="permanent"
      className={clsx(classes.drawer, "menu-layout", {
        [classes.drawerOpen]: open,
        [classes.drawerClose]: !open,
      })}
      classes={{
        paper: clsx(classes.paper, "menu-layout", {
          [classes.drawerOpen]: open,
          [classes.drawerClose]: !open,
        }),
      }}
    >
      <WrapperScrollbar className={clsx(classes.drawerContent)}>
        <div
          className={clsx(classes.toolbar, {
            [classes.toolbarOpen]: open,
            [classes.toolbarClose]: !open,
          })}
        >
          <Link to={"/"} style={{ display: "flex", alignItems: "center" }}>
            {system === "sbs" ? (
              <img
                src={open ? logoBIBlue : logoBISmall}
                style={{ height: 22 }}
              />
            ) : (
              <img src="/favicon.svg" style={{ width: 30 }} />
            )}
          </Link>
          <IconButton onClick={changeOpenMenu} className={classes.menuButton}>
            {theme.direction === "rtl" ? null : (
              <Box>
                <BurgerIcon />
              </Box>
            )}
          </IconButton>
        </div>
        <div
          className={clsx({
            [classes.hide]: !open,
          })}
        >
          <SidebarUserbox />
        </div>
        <EmptyOrShow loading={isFetching}>
          <List style={{ padding: "8px 16px", flexGrow: 1 }}>
            {data?.map(
              ({ menu_action, icon, menu_id, menu_name, sub_menus }) => {
                return (
                  <Fragment key={menu_id}>
                    {!!sub_menus?.length ? (
                      <MenuItem
                        icon={icon}
                        sub_menus={sub_menus}
                        menu_name={menu_name}
                        open={open}
                        setOpen={changeOpenMenu}
                        getIcon={getIcon}
                      />
                    ) : (
                      <Link
                        to={PAGES[menu_action] || "/"}
                        className={clsx(classes.listItem, {
                          [classes.active]:
                            location?.pathname === PAGES[menu_action],
                        })}
                      >
                        <MenuListItem open={open}>
                          <ListItemIcon
                            className={clsx({
                              [classes.mr]: open,
                              [classes.ml]: !open,
                              [classes.itemIconCollapsed]: !open,
                              [classes.active]:
                                window.location?.pathname ===
                                PAGES[menu_action],
                            })}
                          >
                            {getIcon(icon)}
                          </ListItemIcon>
                          {open && <ListItemText primary={menu_name} />}
                        </MenuListItem>
                      </Link>
                    )}
                  </Fragment>
                );
              }
            )}
          </List>
        </EmptyOrShow>
      </WrapperScrollbar>
    </Drawer>
  );
};

export const MenuListItem = ({ open, children, style = {}, ...other }) => {
  return (
    <ListItem
      style={{
        paddingLeft: open ? "10px" : 0,
        paddingRight: open ? "10px" : 0,
        justifyContent: open ? "flex-start" : "center",
        ...style,
      }}
      {...other}
    >
      {children}
    </ListItem>
  );
};

const getIcon = (icon: string) => {
  switch (icon) {
    case "Home":
      return <Home set="light" />;
    case "Wallet":
      return <Wallet set="light" />;
    case "Chart":
      return <Chart set="light" />;
    case "Document":
      return <Document set="light" />;
    case "Chat":
      return <Chat set="light" />;
    case "Paper":
      return <Paper set="light" />;
    case "Search":
      return <Search set="light" />;
    case "Setting":
      return <Setting set="light" />;
    default:
      return null;
  }
};

const BurgerIcon = () => (
  <svg
    width="32"
    height="16"
    viewBox="0 0 32 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect x="6.83862" width="24.1936" height="2" rx="1" fill="#162541" />
    <rect
      x="0.0644531"
      y="7"
      width="30.9677"
      height="2"
      rx="1"
      fill="#162541"
    />
    <rect x="6.83862" y="14" width="24.1936" height="2" rx="1" fill="#162541" />
  </svg>
);
